import apiClient from './client'

import type {
  AdminModule,
  AdminMenuSection,
  AdminPermission,
  AdminRole,
  AdminUserCentroRole,
  AdminUserRolesByUserItem,
  AdminUserRolesSyncPayload,
  CentroModule,
  PaginatedResponse
} from '@/types/gia'

interface ListFilters {
  page?: number
}

const adminApi = {
  modules: {
    list: async (filters: ListFilters = {}): Promise<PaginatedResponse<AdminModule>> => {
      const response = await apiClient.get<PaginatedResponse<AdminModule>>('/admin/modules/', {
        params: filters
      })

      return response.data
    },
    listAll: async (): Promise<AdminModule[]> => {
      const items: AdminModule[] = []
      let page = 1
      let hasMore = true

      while (hasMore) {
        const response = await apiClient.get<PaginatedResponse<AdminModule>>('/admin/modules/', {
          params: { page }
        })
        items.push(...response.data.results)
        hasMore = response.data.next !== null
        page += 1
      }

      return items
    },
    create: async (data: Partial<AdminModule>): Promise<AdminModule> => {
      const response = await apiClient.post<AdminModule>('/admin/modules/', data)
      return response.data
    },
    update: async (id: number, data: Partial<AdminModule>): Promise<AdminModule> => {
      const response = await apiClient.patch<AdminModule>(`/admin/modules/${id}/`, data)
      return response.data
    },
    deactivate: async (id: number): Promise<void> => {
      await apiClient.delete(`/admin/modules/${id}/`)
    }
  },
  menuSections: {
    list: async (filters: ListFilters = {}): Promise<PaginatedResponse<AdminMenuSection>> => {
      const response = await apiClient.get<PaginatedResponse<AdminMenuSection>>('/admin/menu-sections/', {
        params: filters
      })
      return response.data
    },
    listAll: async (): Promise<AdminMenuSection[]> => {
      const items: AdminMenuSection[] = []
      let page = 1
      let hasMore = true

      while (hasMore) {
        const response = await apiClient.get<PaginatedResponse<AdminMenuSection>>('/admin/menu-sections/', {
          params: { page }
        })
        items.push(...response.data.results)
        hasMore = response.data.next !== null
        page += 1
      }

      return items
    },
    create: async (data: Partial<AdminMenuSection>): Promise<AdminMenuSection> => {
      const response = await apiClient.post<AdminMenuSection>('/admin/menu-sections/', data)
      return response.data
    },
    update: async (id: number, data: Partial<AdminMenuSection>): Promise<AdminMenuSection> => {
      const response = await apiClient.patch<AdminMenuSection>(`/admin/menu-sections/${id}/`, data)
      return response.data
    },
    delete: async (id: number): Promise<void> => {
      await apiClient.delete(`/admin/menu-sections/${id}/`)
    }
  },
  roles: {
    list: async (filters: ListFilters = {}): Promise<PaginatedResponse<AdminRole>> => {
      const response = await apiClient.get<PaginatedResponse<AdminRole>>('/admin/roles/', {
        params: filters
      })
      return response.data
    },
    listAll: async (): Promise<AdminRole[]> => {
      const items: AdminRole[] = []
      let page = 1
      let hasMore = true

      while (hasMore) {
        const response = await apiClient.get<PaginatedResponse<AdminRole>>('/admin/roles/', {
          params: { page }
        })
        items.push(...response.data.results)
        hasMore = response.data.next !== null
        page += 1
      }

      return items
    },
    create: async (data: Partial<AdminRole> & { permission_codes?: string[] }): Promise<AdminRole> => {
      const response = await apiClient.post<AdminRole>('/admin/roles/', data)
      return response.data
    },
    update: async (id: number, data: Partial<AdminRole> & { permission_codes?: string[] }): Promise<AdminRole> => {
      const response = await apiClient.put<AdminRole>(`/admin/roles/${id}/`, data)
      return response.data
    },
    delete: async (id: number): Promise<void> => {
      await apiClient.delete(`/admin/roles/${id}/`)
    }
  },
  permissions: {
    list: async (
      filters: ListFilters & { module?: number; module_code?: string } = {}
    ): Promise<PaginatedResponse<AdminPermission>> => {
      const response = await apiClient.get<PaginatedResponse<AdminPermission>>('/admin/permissions/', {
        params: filters
      })
      return response.data
    },
    listAll: async (): Promise<AdminPermission[]> => {
      const items: AdminPermission[] = []
      let page = 1
      let hasMore = true

      while (hasMore) {
        const response = await apiClient.get<PaginatedResponse<AdminPermission>>('/admin/permissions/', {
          params: { page }
        })
        items.push(...response.data.results)
        hasMore = response.data.next !== null
        page += 1
      }

      return items
    },
    create: async (data: Partial<AdminPermission>): Promise<AdminPermission> => {
      const response = await apiClient.post<AdminPermission>('/admin/permissions/', data)
      return response.data
    },
    update: async (id: number, data: Partial<AdminPermission>): Promise<AdminPermission> => {
      const response = await apiClient.put<AdminPermission>(`/admin/permissions/${id}/`, data)
      return response.data
    },
    delete: async (id: number): Promise<void> => {
      await apiClient.delete(`/admin/permissions/${id}/`)
    }
  },
  userRoles: {
    list: async (
      filters: ListFilters & { user?: number; centro?: number; role?: number } = {}
    ): Promise<PaginatedResponse<AdminUserCentroRole>> => {
      const response = await apiClient.get<PaginatedResponse<AdminUserCentroRole>>('/admin/user-roles/', {
        params: filters
      })
      return response.data
    },
    create: async (data: Partial<AdminUserCentroRole>): Promise<AdminUserCentroRole> => {
      const response = await apiClient.post<AdminUserCentroRole>('/admin/user-roles/', data)
      return response.data
    },
    update: async (id: number, data: Partial<AdminUserCentroRole>): Promise<AdminUserCentroRole> => {
      const response = await apiClient.put<AdminUserCentroRole>(`/admin/user-roles/${id}/`, data)
      return response.data
    },
    delete: async (id: number): Promise<void> => {
      await apiClient.delete(`/admin/user-roles/${id}/`)
    },
    byUser: async (
      filters: ListFilters & { q?: string; centro?: number | 'global'; role?: number; scope?: 'global' | 'centro' } = {}
    ): Promise<PaginatedResponse<AdminUserRolesByUserItem>> => {
      const response = await apiClient.get<PaginatedResponse<AdminUserRolesByUserItem>>('/admin/user-roles/by-user/', {
        params: filters
      })
      return response.data
    },
    userSummary: async (user: number): Promise<AdminUserRolesByUserItem> => {
      const response = await apiClient.get<AdminUserRolesByUserItem>('/admin/user-roles/user-summary/', {
        params: { user }
      })
      return response.data
    },
    syncUser: async (payload: AdminUserRolesSyncPayload): Promise<AdminUserRolesByUserItem> => {
      const response = await apiClient.post<AdminUserRolesByUserItem>('/admin/user-roles/sync-user/', payload)
      return response.data
    }
  },
  centroModules: {
    list: async (centroId?: number): Promise<CentroModule[]> => {
      const response = await apiClient.get<{ results: CentroModule[] } | CentroModule[]>('/centro-modules/', {
        params: centroId ? { centro: centroId } : undefined
      })
      const data = response.data
      return Array.isArray(data) ? data : data.results ?? []
    },
    available: async (
      centroId: number
    ): Promise<{ id?: number | null; module: AdminModule; enabled: boolean; settings: Record<string, unknown> }[]> => {
      const response = await apiClient.get<{ results: unknown[] } | unknown[]>('/centro-modules/available/', {
        params: { centro: centroId }
      })
      const data = response.data
      return (Array.isArray(data) ? data : data.results ?? []) as {
        id?: number | null
        module: AdminModule
        enabled: boolean
        settings: Record<string, unknown>
      }[]
    },
    upsert: async (data: Partial<CentroModule>): Promise<CentroModule> => {
      if (data.id) {
        const response = await apiClient.put<CentroModule>(`/centro-modules/${data.id}/`, data)
        return response.data
      }

      try {
        const response = await apiClient.post<CentroModule>('/centro-modules/', data)
        return response.data
      } catch (error) {
        if (data.centro && data.module) {
          const listResponse = await apiClient.get<{ results: CentroModule[] } | CentroModule[]>('/centro-modules/', {
            params: { centro: data.centro }
          })
          const listData = Array.isArray(listResponse.data) ? listResponse.data : listResponse.data.results ?? []
          const existing = listData.find(item => item.module === data.module)
          if (existing?.id) {
            const response = await apiClient.put<CentroModule>(`/centro-modules/${existing.id}/`, data)
            return response.data
          }
        }
        throw error
      }
    }
  }
}

export default adminApi
